import GridContainer from '@/components/atoms/ResponsiveGrid/GridContainer';
import GridItem from '@/components/atoms/ResponsiveGrid/GridItem';
import Link from '@/components/atoms/legacy/Link/Link';
import { ResponsiveMargin } from '@/components/atoms/legacy/ResponsiveMargin/ResponsiveMargin';
import FrequentlyAskedQuestions from '@/components/organisms/FrequentlyAskedQuestions';
import { SEOMetaTags } from '@/lib/seo/seoTags';
import { BasePageProps } from '@/types/page';

export default function FAQ() {
  return (
    <main className="bg-neutral-100 pb-xl">
      <ResponsiveMargin>
        <GridContainer className="w-full justify-center">
          <GridItem xs={12} m={8} colStartXs={0} colStartM={3}>
            <div className="mt-10 w-full justify-center gap-m rounded-lg bg-white p-l">
              <FrequentlyAskedQuestions
                newUI
                subHeading="Everything you need to know about EV.com"
                questionGroups={[
                  {
                    category: 'Searching and buying vehicles',
                    questions: [
                      {
                        header: `How do I search for an electric vehicle for sale?`,
                        text: `Visit our inventory page to search for electric vehicles and plug-in hybrids from our trusted dealership partners. You can easily customize your search by adjusting the filters for make, model, year, price, mileage, and seller type. This will help you find the best EV that meets your needs.`,
                      },
                      {
                        header: `What is the difference between a Custom Order and an Inventory vehicle?`,
                        text:
                          `Custom Order vehicles are those that you can personalize according to your preferences. Our dealership partners will contact you to help facilitate your order and guide you through the customization process of your brand-new EV. Depending on the vehicle you choose, EV.com will connect you with the right dealership partner in your area.\n\n` +
                          `Inventory vehicles are pre-configured and available for immediate purchase from our trusted dealership partners. These vehicles are listed on our inventory page and can be filtered based on your specific requirements.`,
                      },
                      {
                        header: `What is the difference between a new and used? `,
                        text: (
                          <>
                            <p>
                              <b>New vehicles</b> have not been previously owned
                              or titled and typically have zero or very low
                              mileage. Dealerships may have a limited supply of
                              new vehicles available.
                            </p>
                            {'\n'}
                            <p>
                              <b>Used vehicles</b> have been previously owned
                              and usually have some mileage and signs of wear
                              and tear. EV.com partners with trustworthy dealers
                              to ensure a reliable shopping experience for used
                              vehicles.
                            </p>
                          </>
                        ),
                      },
                      {
                        header: `What is the difference between Hertz, Franchise Dealers, and Independent Dealers?`,
                        text: (
                          <>
                            <p>
                              EV.com has partnered with many dealers nationwide
                              to bring you the best deals and help you find your
                              next EV. We have three types of sellers on the
                              platform:
                            </p>
                            {'\n'}
                            <p>
                              <b>Hertz:</b> Hertz specializes in rental cars and
                              lists ex-rental EVs on EV.com through Hertz Car
                              Sales. These vehicles may have higher mileage than
                              the average used EV but are often priced below the
                              average market rate.
                            </p>
                            {'\n'}
                            <p>
                              <b>Franchise Dealers:</b> Franchise dealers are
                              companies or groups affiliated with specific makes
                              and manufacturers, such as your local Ford or
                              Chevrolet dealership.
                            </p>
                            {'\n'}
                            <p>
                              <b>Independent Dealers:</b> Independent dealers
                              are smaller, privately-owned dealerships that sell
                              various makes and models without direct
                              affiliation to any specific vehicle manufacturer
                              or brand.
                            </p>
                          </>
                        ),
                      },
                      {
                        header: `Who are Hertz and why are Hertz vehicles listed on EV.com?`,
                        text: `Hertz specializes in rental cars and lists ex-rental EVs on EV.com through Hertz Car Sales. These vehicles may have higher mileage than the average used EV but are often priced below the average market rate. Hertz offers no-haggle pricing, ensuring customers know the final price they will pay to purchase the vehicle.`,
                      },
                      {
                        header: `How do I contact the dealer who is selling the vehicle?`,
                        text: `EV.com makes it simple to contact the seller of a listed vehicle. Click the "Check Availability" button or call the seller using the phone number displayed behind the "Call" button on the vehicle's listing.`,
                      },
                    ],
                  },
                  {
                    category: 'Resources and Useful Information',
                    questions: [
                      {
                        header: `How do I know if I will qualify for the incentives?`,
                        text: (
                          <p>
                            Your eligibility for EV incentives is determined by
                            the type of vehicle you plan to purchase, the sale
                            price, and your individual tax circumstances. For
                            more information on incentives, please visit our{' '}
                            <Link
                              href="/news#newsCategory"
                              className="inline-flex text-blue-medium"
                            >
                              News and Buyers Guide
                            </Link>{' '}
                            section.
                          </p>
                        ),
                      },
                      {
                        header: `Where can I find information on EV Charging?`,
                        text: (
                          <p>
                            Electric Vehicle Charging is improving every year,
                            with new advancements in battery technology and more
                            charging stations being built nationwide. Our{' '}
                            <Link
                              href="/news"
                              className="inline-flex text-blue-medium"
                            >
                              News
                            </Link>{' '}
                            section and specific articles on EV charging explain
                            distinct types of charging.
                          </p>
                        ),
                      },
                      {
                        header: `Where can find information on EV Insurance?`,
                        text: (
                          <p>
                            Information on electric vehicle charging, including
                            advancements in battery technology and the growing
                            network of charging stations, can be found in our{' '}
                            <Link
                              href="/news"
                              className="inline-flex text-blue-medium"
                            >
                              News
                            </Link>{' '}
                            section. We have specific articles that explain the
                            different types of EV charging.
                          </p>
                        ),
                      },
                      {
                        header: `Where can I find information on EV batteries?`,
                        text: (
                          <p>
                            For the latest articles and news on electric vehicle
                            batteries, visit our{' '}
                            <Link
                              href="/news#newsCategory"
                              className="inline-flex text-blue-medium"
                            >
                              News and Buyers Guide
                            </Link>{' '}
                            section.
                          </p>
                        ),
                      },
                      {
                        header: `Are warranties available?`,
                        text: `Some vehicles have manufacturer warranties on the vehicle and battery that may still be valid depending on the vehicle’s age and mileage. We recommend contacting the seller or dealership listing the vehicle to inquire about remaining warranty coverage. Additionally, our dealer partners may offer warranty packages that can be purchased as extras when shopping for a used EV.`,
                      },
                      {
                        header: `A dealer hasn’t responded to me, what should I do?`,
                        text: `If a dealership has not responded to your request for availability, we recommend calling the dealership directly using the phone number provided on the vehicle listing on EV.com.`,
                      },
                      {
                        header: `How do I save a vehicle?`,
                        text: `You can save any vehicle listing to your profile by clicking on the heart icon on a vehicle listing or from inside the vehicle detail page. Your saved vehicles will be displayed in your profile section on EV.com, allowing you to keep track of the EVs you are interested in.`,
                      },
                      {
                        header: `Can I negotiate with a dealer on price?`,
                        text: `For vehicles with a "No haggle price" displayed on the listing, the EV is already listed at the lowest sale price offered, and no negotiation is necessary. In all other instances, you can contact the seller to discuss the price of the vehicle.`,
                      },
                      {
                        header: `How do I buy an EV from the website?`,
                        text: `EV.com does not currently offer an online checkout or payment system to purchase a vehicle directly from the website. Please contact the seller through EV.com to arrange the purchase of a vehicle.`,
                      },
                    ],
                  },
                  {
                    category: 'Account and Privacy',
                    questions: [
                      {
                        header: `Does EV.com share my data with dealerships?`,
                        text: `EV.com only shares your name and the contact information you provided in your profile with the sellers of the vehicles you have messaged through the website.`,
                      },
                      {
                        header: `How do I reset my EV.com password?`,
                        text: `You can reset your password by following the instructions provided on the EV.com website.`,
                      },
                      {
                        header: `How do I change my EV.com account settings?`,
                        text: `To change your EV.com account settings, log in to your profile and navigate to your account settings.`,
                      },
                    ],
                  },
                  {
                    category: 'EV.com Business',
                    questions: [
                      {
                        header: `What is EV.com and how does it work?`,
                        text: `EV.com is an online electric vehicle marketplace that connects buyers with dealerships and sellers nationwide, helping them find their perfect electric vehicle. Customers can browse thousands of vehicles from multiple dealerships, contact sellers through EV.com to inquire about details, arrange test drives, and purchase vehicles.`,
                      },
                      {
                        header: `Is EV.com free to use? `,
                        text: `Yes, EV.com is free for customers to use. You can browse the extensive online inventory of electric vehicles and contact sellers at no cost.`,
                      },
                      {
                        header: `Does EV.com own the electric cars that are for sale?`,
                        text: `No, EV.com does not own the vehicles listed on the website. Instead, EV.com works with trusted dealerships and sellers nationwide to list vehicles for sale at our partnership locations.`,
                      },
                      {
                        header: `Which countries and U.S. states does EV.com operate in?`,
                        text: (
                          <i>
                            As of 2024, EV.com is currently only available in
                            the United States.
                          </i>
                        ),
                      },
                      {
                        header: `Which dealerships are listed on EV.com?`,
                        text: `EV.com has partnered with an extensive list of trusted dealerships and sellers across the U.S. The website continues to expand its network of partners, adding thousands of dealerships to create the largest electric vehicle marketplace in the world.`,
                      },
                      {
                        header: `I’m a dealer interested in working with EV.com, how do I sign up?`,
                        text: `Dealerships and partner companies can contact our sales team to set up an introduction call by emailing hi@ev.com.`,
                      },
                      {
                        header: `I’m interested in careers at EV.com, are there job openings?`,
                        text: (
                          <p>
                            EV.com is continuously looking for the brightest and
                            most enthusiastic team members who are passionate
                            about electric vehicles, the environment, and
                            joining a cutting-edge company. Please visit our
                            LinkedIn page to see the latest job opportunities:{' '}
                            <Link
                              href="https://www.linkedin.com/company/ev-com"
                              className="inline-flex text-blue-medium"
                            >
                              EV.com LinkedIn Page.
                            </Link>
                          </p>
                        ),
                      },
                    ],
                  },
                ]}
              />
            </div>
          </GridItem>
        </GridContainer>
      </ResponsiveMargin>
    </main>
  );
}

export const getStaticProps = async () => {
  return {
    props: {
      metadata: {
        title: SEOMetaTags.faqPage.title,
        description: SEOMetaTags.faqPage.description,
      },
    } satisfies BasePageProps,
  };
};
